import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminAdvanced.css';

function CaseManagement() {
  const [cases, setCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statsData, setStatsData] = useState({
    totalCases: 0,
    completedCases: 0,
    inProgressCases: 0,
    categoriesAvgScores: {},
  });
  
  // à‰tats pour le filtrage et la recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [filterByStatus, setFilterByStatus] = useState('all');
  const [filterByCategory, setFilterByCategory] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');

  useEffect(() => {
    fetchCases();
  }, []);

  // Effet pour filtrer les cas lorsque les critères de filtre changent
  useEffect(() => {
    const filtered = cases.filter(caseItem => {
      const matchesSearch = caseItem.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          caseItem.activity_sector?.toLowerCase().includes(searchTerm.toLowerCase());
    
      const status = getCaseStatus(caseItem);
      const matchesStatus = filterByStatus === 'all' || 
                          (filterByStatus === 'completed' && status === 'Complété') ||
                          (filterByStatus === 'in_progress' && status === 'En cours') ||
                          (filterByStatus === 'not_started' && status === 'Non démarré');
    
      return matchesSearch && matchesStatus;
    });
    setFilteredCases(filtered);
  }, [cases, searchTerm, filterByStatus, filterByCategory]);

  const fetchCases = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/all-cases`);
      const casesData = response.data;
      setCases(casesData);
      setFilteredCases(casesData);
      
      // Calculer les statistiques à  partir des données des cas
      updateStatsData(casesData);
      
      setError(null);
    } catch (err) {
      console.error("Erreur lors de la récupération des cas:", err);
      setError("Impossible de charger les données des cas. Utilisation des données de démonstration.");
      // Utiliser des données de démonstration en cas d'erreur
      const demoData = generateDemoCases(15); // Générer 15 cas de démonstration
      setCases(demoData);
      setFilteredCases(demoData);
      
      // Calculer les statistiques pour les données de démo
      updateStatsData(demoData);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fonction pour générer des données de démonstration
  const generateDemoCases = (count = 15) => {
    const sectors = ['Industrie', 'Services', 'Agroalimentaire', 'Technologie', 'Santé'];
    const cases = [];
    
    for (let i = 0; i < count; i++) {
      const caseData = {
        _id: `demo-case-${i}`,
        company_name: `Entreprise ${i+1}`,
        activity_sector: sectors[Math.floor(Math.random() * sectors.length)],
        company_size: Math.floor(Math.random() * 500) + 10,
        timestamp: new Date(Date.now() - Math.floor(Math.random() * 10000000000)).toISOString(),
        userId: `user-${Math.floor(Math.random() * 10) + 1}`,
        
        // Générer des scores aléatoires pour chaque catégorie
        environmental: { Total: Math.floor(Math.random() * 10) },
        social: { Total: Math.floor(Math.random() * 10) },
        modernization: { Total: Math.floor(Math.random() * 10) },
        leadTime: { Total: Math.floor(Math.random() * 10) },
        cost: { Total: Math.floor(Math.random() * 10) },
        quality: { Total: Math.floor(Math.random() * 10) },
        clientConsumer: { Total: Math.floor(Math.random() * 10) }
      };
      
      // Certains cas auront des scores à  0 pour simuler des cas non démarrés ou partiellement complétés
      if (i % 3 === 0) {
        caseData.environmental.Total = 0;
        caseData.social.Total = 0;
      } else if (i % 5 === 0) {
        caseData.quality.Total = 0;
        caseData.clientConsumer.Total = 0;
      }
      
      cases.push(caseData);
    }
    
    return cases;
  };

  const handleCaseSelect = (caseData) => {
    setSelectedCase(caseData);
    setError('');
    setSuccess('');
  };

  const handleRefreshScores = async (caseId) => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/refresh-scores/${caseId}`,
        {},
        {
          headers: {
            'Authorization': 'Bearer admin',
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
          }
        }
      );
      
      setSuccess(`Les scores du cas ont été recalculés avec succès.`);
      fetchCases(); // Recharger tous les cas pour mettre à  jour la liste
      
    } catch (error) {
      console.error('Erreur lors du recalcul des scores:', error);
      setError('Erreur lors du recalcul des scores. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour obtenir le statut d'un cas en fonction des scores
  const getCaseStatus = (caseItem) => {
    if (!caseItem) return 'Non démarré';
    
    // Vérifier quelles catégories sont présentes dans ce cas
    const presentCategories = ['environmental', 'social', 'modernization', 'leadTime', 'cost', 'quality', 'clientConsumer']
      .filter(category => caseItem[category] && typeof caseItem[category] === 'object');
    
    // Si aucune catégorie n'est présente, considérer comme non démarré
    if (presentCategories.length === 0) return 'Non démarré';
    
    // Vérifier si tous les scores sont à  0 pour les catégories présentes
    const allZero = presentCategories.every(category => 
      !caseItem[category].Total || caseItem[category].Total === 0
    );
    
    if (allZero) return 'Non démarré';
    
    // Compter combien de catégories ont un score supérieur à  0
    const categoriesWithScores = presentCategories.filter(category => 
      caseItem[category] && caseItem[category].Total > 0
    );
    
    // Si au moins 60% des catégories présentes ont un score, considérer comme complété
    const completionRatio = categoriesWithScores.length / presentCategories.length;
    
    // Un cas est complété si au moins 60% des catégories disponibles ont un score
    return completionRatio >= 0.6 ? 'Complété' : 'En cours';
  };
  
  // Formatage de la date
  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Trier les cas
  const sortedCases = [...filteredCases].sort((a, b) => {
    switch (sortBy) {
      case 'date_asc':
        return new Date(a.timestamp || a.date_created) - new Date(b.timestamp || b.date_created);
      case 'date_desc':
        return new Date(b.timestamp || b.date_created) - new Date(a.timestamp || a.date_created);
      case 'name_asc':
        return (a.company_name || '').localeCompare(b.company_name || '');
      case 'name_desc':
        return (b.company_name || '').localeCompare(a.company_name || '');
      default:
        return 0;
    }
  });

  // Rendu des statistiques
  const renderCaseStats = () => {
    return (
      <div className="case-stats">
        <div className="stats-card">
          <h3>Total des cas</h3>
          <p className="stats-value">{statsData.totalCases}</p>
        </div>
        <div className="stats-card">
          <h3>Cas complétés</h3>
          <p className="stats-value">{statsData.completedCases}</p>
        </div>
        <div className="stats-card">
          <h3>Cas en cours</h3>
          <p className="stats-value">{statsData.inProgressCases}</p>
        </div>
        <div className="stats-card">
          <h3>Taux de complétion</h3>
          <p className="stats-value">
            {statsData.totalCases > 0 
              ? `${Math.round((statsData.completedCases / statsData.totalCases) * 100)}%` 
              : '0%'}
          </p>
        </div>
      </div>
    );
  };

  // Rendu du graphique des scores moyens par catégorie
  const renderCategoryScores = () => {
    const categories = {
      environmental: 'Environnement',
      social: 'Social',
      modernization: 'Modernisation',
      leadTime: 'Délai',
      cost: 'Coà»t',
      quality: 'Qualité',
      clientConsumer: 'Client/Consommateur'
    };
    
    const scores = statsData.categoriesAvgScores;
    
    if (!scores || Object.keys(scores).length === 0) {
      return <div className="empty-chart-message">Aucune donnée disponible</div>;
    }
    
    // Trouver le score maximum pour dimensionner correctement les barres
    const maxScore = Math.max(...Object.values(scores));
    
    return (
      <div className="category-scores-chart">
        <h3>Scores moyens par catégorie</h3>
        <div className="bar-chart">
          {Object.entries(scores).map(([category, score], index) => (
            <div key={index} className="bar-chart-item">
              <div className="bar-chart-label">{categories[category] || category}</div>
              <div className="bar-chart-bar-container">
                <div 
                  className="bar-chart-bar" 
                  style={{ 
                    width: `${(score / maxScore) * 100}%`,
                    minWidth: score > 0 ? '2%' : '0'
                  }}
                ></div>
                <span className="bar-chart-value">{score.toFixed(1)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Fonction pour calculer et mettre à  jour les statistiques
  const updateStatsData = (casesData) => {
    if (!casesData || casesData.length === 0) {
      setStatsData({
        totalCases: 0,
        completedCases: 0,
        inProgressCases: 0,
        categoriesAvgScores: {},
      });
      return;
    }
    
    // Calculer le nombre total de cas
    const totalCases = casesData.length;
    
    // Calculer le nombre de cas par statut
    let completedCount = 0;
    let inProgressCount = 0;
    
    // Calculer les scores moyens par catégorie
    const categoryScores = {
      environmental: [],
      social: [],
      modernization: [],
      leadTime: [],
      cost: [],
      quality: [],
      clientConsumer: []
    };
    
    casesData.forEach(caseItem => {
      const status = getCaseStatus(caseItem);
      if (status === 'Complété') {
        completedCount++;
      } else if (status === 'En cours') {
        inProgressCount++;
      }
      
      // Collecter les scores par catégorie
      Object.entries(categoryScores).forEach(([category, scores]) => {
        if (caseItem[category] && typeof caseItem[category].Total === 'number') {
          scores.push(caseItem[category].Total);
        }
      });
    });
    
    // Calculer les moyennes pour chaque catégorie
    const categoriesAvgScores = {};
    Object.entries(categoryScores).forEach(([category, scores]) => {
      if (scores.length > 0) {
        const sum = scores.reduce((acc, score) => acc + score, 0);
        categoriesAvgScores[category] = parseFloat((sum / scores.length).toFixed(1));
      } else {
        categoriesAvgScores[category] = 0;
      }
    });
    
    // Mettre à  jour l'état des statistiques
    setStatsData({
      totalCases,
      completedCases: completedCount,
      inProgressCases: inProgressCount,
      categoriesAvgScores
    });
  };

  return (
    <div className="case-management-container">
      <h2>Gestion des Cas</h2>
      
      {error && <div className="admin-error">{error}</div>}
      {success && <div className="admin-success">{success}</div>}
      
      {/* Statistiques des cas */}
      {renderCaseStats()}
      
      {/* Graphique des scores moyens */}
      {renderCategoryScores()}
      
      {/* Filtres et options de tri */}
      <div className="filters-container">
        <div className="search-filter">
          <input
            type="text"
            placeholder="Rechercher une entreprise..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="status-filter">
          <label>Statut:</label>
          <select 
            value={filterByStatus}
            onChange={(e) => setFilterByStatus(e.target.value)}
          >
            <option value="all">Tous</option>
            <option value="completed">Complétés</option>
            <option value="in_progress">En cours</option>
            <option value="not_started">Non démarrés</option>
          </select>
        </div>
        
        <div className="sort-options">
          <label>Trier par:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="date_desc">Date (récent â†’ ancien)</option>
            <option value="date_asc">Date (ancien â†’ récent)</option>
            <option value="name_asc">Nom (A â†’ Z)</option>
            <option value="name_desc">Nom (Z â†’ A)</option>
          </select>
        </div>
      </div>
      
      {/* Liste des cas et détails */}
      <div className="case-management-layout">
        <div className="cases-list-container">
          <h3>Liste des Cas ({sortedCases.length})</h3>
          
          {isLoading ? (
            <div className="loading-indicator">Chargement des cas...</div>
          ) : sortedCases.length === 0 ? (
            <div className="empty-list-message">Aucun cas trouvé</div>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Entreprise</th>
                    <th>Secteur</th>
                    <th>Taille</th>
                    <th>Date</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCases.map((caseItem, index) => (
                    <tr 
                      key={index} 
                      className={selectedCase && selectedCase._id === caseItem._id ? 'selected-row' : ''}
                    >
                      <td>{caseItem.company_name || 'Non spécifié'}</td>
                      <td>{caseItem.activity_sector || 'Non spécifié'}</td>
                      <td>{caseItem.company_size || 'N/A'}</td>
                      <td>{formatDate(caseItem.timestamp || caseItem.date_created)}</td>
                      <td>
                        <span className={`status-badge ${getCaseStatus(caseItem).toLowerCase().replace(/\s+/g, '-')}`}>
                          {getCaseStatus(caseItem)}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="admin-btn admin-btn-primary"
                          onClick={() => handleCaseSelect(caseItem)}
                        >
                          Détails
                        </button>
                        <button 
                          className="admin-btn admin-btn-warning"
                          onClick={() => handleRefreshScores(caseItem._id)}
                          disabled={isLoading}
                        >
                          Recalculer scores
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Détails du cas sélectionné */}
        {selectedCase && (
          <div className="case-details-panel">
            <h3>Détails du Cas</h3>
            
            <div className="case-details-header">
              <h4>{selectedCase.company_name || 'Entreprise sans nom'}</h4>
              <span className={`status-badge ${getCaseStatus(selectedCase).toLowerCase().replace(/\s+/g, '-')}`}>
                {getCaseStatus(selectedCase)}
              </span>
            </div>
            
            <div className="case-details-content">
              <div className="case-details-field">
                <span className="case-details-label">ID:</span>
                <span className="case-details-value">{selectedCase._id}</span>
              </div>
              <div className="case-details-field">
                <span className="case-details-label">Secteur:</span>
                <span className="case-details-value">{selectedCase.activity_sector || 'Non spécifié'}</span>
              </div>
              <div className="case-details-field">
                <span className="case-details-label">Taille:</span>
                <span className="case-details-value">{selectedCase.company_size || 'Non spécifié'} employés</span>
              </div>
              <div className="case-details-field">
                <span className="case-details-label">Date de création:</span>
                <span className="case-details-value">{formatDate(selectedCase.date_created || selectedCase.timestamp)}</span>
              </div>
              <div className="case-details-field">
                <span className="case-details-label">Utilisateur:</span>
                <span className="case-details-value">{selectedCase.userId || 'Non assigné'}</span>
              </div>
            </div>
            
            <div className="case-scores-section">
              <h4>Scores par catégorie</h4>
              <div className="scores-grid">
                {['environmental', 'social', 'modernization', 'leadTime', 'cost', 'quality', 'clientConsumer'].map((category, idx) => {
                  const categoryNames = {
                    'environmental': 'Environnement',
                    'social': 'Social',
                    'modernization': 'Modernisation',
                    'leadTime': 'Délai',
                    'cost': 'Coà»t',
                    'quality': 'Qualité',
                    'clientConsumer': 'Client/Consommateur'
                  };
                  
                  const score = selectedCase[category]?.Total || 0;
                  
                  return (
                    <div key={idx} className="score-card">
                      <h5>{categoryNames[category]}</h5>
                      <div className="score-value">{score}</div>
                      <div className="score-bar-container">
                        <div 
                          className="score-bar"
                          style={{ width: `${Math.min(100, score)}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="case-actions">
              <button 
                className="admin-btn admin-btn-success"
                onClick={() => window.open(`/case-details/${selectedCase._id}`, '_blank')}
              >
                Voir page complète
              </button>
              <button 
                className="admin-btn admin-btn-warning"
                onClick={() => handleRefreshScores(selectedCase._id)}
                disabled={isLoading}
              >
                Recalculer les scores
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CaseManagement; 